import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode } from "vue"

const _hoisted_1 = ["data-test-ref"]
const _hoisted_2 = { class: "h2 mb-4" }
const _hoisted_3 = { class: "h3" }
const _hoisted_4 = { key: 0 }
const _hoisted_5 = ["innerHTML"]
const _hoisted_6 = { key: 1 }
const _hoisted_7 = ["innerHTML"]
const _hoisted_8 = { key: 0 }
const _hoisted_9 = ["innerHTML"]
const _hoisted_10 = ["innerHTML"]

import { computed, PropType, ref, watch } from 'vue';
import { mergeMessages, useI18n } from '@/i18n';
import staticLinks from '@/config/config.json';
import { LocaleMessages } from 'vue-i18n';
import { useAppStore } from '@/store';
import { LoginModuleConfiguration } from '@/types';

interface DetailedBlock {
    title: string;
    content: string;
    blocks?: string[];
}

type FaqBlock = string | string[] | DetailedBlock[];

interface FaqSection {
    section: string;
    title: string;
    blocks: FaqBlock[];
}

/*-----*\
| Props |
\*-----*/


const __default__ = {
    compatConfig: {
        COMPONENT_V_MODEL: false,
    },
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'faq-modal',
  props: {
    modelValue: {
        type: Boolean,
        default: false,
    },
    testRef: {
        type: String,
        required: true
    },
    i18nMessages: {
        type: Object as PropType<LocaleMessages<unknown>>,
        default: () => ({}),
    }
},
  emits: ['update:modelValue'],
  setup(__props, { emit }) {

const props = __props;

const {
    forgotPassword: forgotPasswordLink,
    forgotUsername: forgotUsernameLink,
    supportPhone: supportPhoneLink,
} = staticLinks;

const getTechnicalSupportLink = () => {
    const siteConfiguration = appStore.siteConfiguration as LoginModuleConfiguration;
    return siteConfiguration?.nonSecureUrls?.technicalSupport;
};

const i18n = useI18n();
const appStore = useAppStore();
const { rt, t, tm } = i18n;



/*-----*\
| Emits |
\*-----*/



/*----*\
| Data |
\*----*/

const showModal = ref(false);

/*--------*\
| Computed |
\*--------*/

const faqSections = computed<FaqSection[]>(() => {
    return tm('faq.content');
});

const links = computed(() => {
    const linksMessage: Record<string, string> = tm('faq.links');
    const linkMap = {
        forgotUsername: forgotUsernameLink,
        forgotPassword: forgotPasswordLink,
        supportPhone: supportPhoneLink,
        visitTechnicalSupport: getTechnicalSupportLink(),
    };
    return Object.keys(linksMessage).reduce((accumulator, currentValue) => {
        const href = linkMap[currentValue as keyof typeof linkMap];
        const target = href.startsWith('http') ? '_blank' : '_self';
        accumulator[currentValue] = `<a href="${href}" target="${target}">${linksMessage[currentValue]}</a>`;
        return accumulator;
    }, {} as Record<string, string>);
});

/*--------*\
| Watchers |
\*--------*/

watch(() => props.modelValue, (value) => {
    showModal.value = value;
}, { immediate: true });

watch(() => props.i18nMessages, (i18nMessages) => {
    mergeMessages(i18n, i18nMessages);
}, { immediate: true });

/*-------*\
| Methods |
\*-------*/

function isParagraphBlock(block: FaqBlock) {
    return Object.prototype.toString.call(block) === '[object String]';
}

function isBulletListBlock(block: FaqBlock) {
    return Object.prototype.toString.call(block) === '[object Array]';
}

function isDetailedBlock(block: string | string[] | DetailedBlock) {
    return Object.prototype.toString.call(block) === '[object Object]';
}


return (_ctx: any,_cache: any) => {
  const _component_b_modal = _resolveComponent("b-modal")!

  return (showModal.value)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        "data-test-ref": `${props.testRef}-faq-modal`
      }, [
        _createVNode(_component_b_modal, {
          title: _unref(t)('faq.title'),
          "title-class": "h2",
          size: "lg",
          visible: showModal.value,
          "ok-only": "",
          onShown: _cache[0] || (_cache[0] = ($event: any) => (emit('update:modelValue', true))),
          onHidden: _cache[1] || (_cache[1] = ($event: any) => (emit('update:modelValue', false)))
        }, {
          default: _withCtx(() => [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(faqSections), (faqSection) => {
              return (_openBlock(), _createElementBlock(_Fragment, { key: faqSection }, [
                _createElementVNode("h1", _hoisted_2, _toDisplayString(faqSection.section), 1 /* TEXT */),
                _createElementVNode("h2", _hoisted_3, _toDisplayString(faqSection.title), 1 /* TEXT */),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(faqSection.blocks, (block1) => {
                  return (_openBlock(), _createElementBlock(_Fragment, { key: block1 }, [
                    (isParagraphBlock(block1))
                      ? (_openBlock(), _createElementBlock("p", _hoisted_4, [
                          _createElementVNode("span", {
                            innerHTML: _unref(rt)(block1 as string, _unref(links))
                          }, null, 8 /* PROPS */, _hoisted_5)
                        ]))
                      : (isBulletListBlock(block1))
                        ? (_openBlock(), _createElementBlock("ul", _hoisted_6, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(block1, (block2, block2Index) => {
                              return (_openBlock(), _createElementBlock("li", {
                                key: `${block2Index} ${block2}`,
                                class: _normalizeClass(isDetailedBlock(block2) ? 'mb-3' : '')
                              }, [
                                (isDetailedBlock(block2))
                                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                      _createElementVNode("strong", null, _toDisplayString((block2 as DetailedBlock).title), 1 /* TEXT */),
                                      _createElementVNode("span", {
                                        innerHTML: _unref(rt)((block2 as DetailedBlock).content, _unref(links))
                                      }, null, 8 /* PROPS */, _hoisted_7),
                                      ((block2 as DetailedBlock).blocks)
                                        ? (_openBlock(), _createElementBlock("ul", _hoisted_8, [
                                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList((block2 as DetailedBlock).blocks, (block3) => {
                                              return (_openBlock(), _createElementBlock("li", {
                                                key: block3,
                                                class: "mt-3",
                                                innerHTML: _unref(rt)(block3 as string, _unref(links))
                                              }, null, 8 /* PROPS */, _hoisted_9))
                                            }), 128 /* KEYED_FRAGMENT */))
                                          ]))
                                        : _createCommentVNode("v-if", true)
                                    ], 64 /* STABLE_FRAGMENT */))
                                  : (_openBlock(), _createElementBlock("span", {
                                      key: 1,
                                      innerHTML: _unref(rt)(block2 as string, _unref(links))
                                    }, null, 8 /* PROPS */, _hoisted_10))
                              ], 2 /* CLASS */))
                            }), 128 /* KEYED_FRAGMENT */))
                          ]))
                        : _createCommentVNode("v-if", true)
                  ], 64 /* STABLE_FRAGMENT */))
                }), 128 /* KEYED_FRAGMENT */))
              ], 64 /* STABLE_FRAGMENT */))
            }), 128 /* KEYED_FRAGMENT */))
          ], undefined, true),
          _: 1 /* STABLE */
        }, 8 /* PROPS */, ["title", "visible"])
      ], 8 /* PROPS */, _hoisted_1))
    : _createCommentVNode("v-if", true)
}
}

})